"use client";

import React from "react";
import { Title, Description } from "@/components/Common/Typography";
import {
  CreditCard,
  Clock,
  Calendar,
  Globe,
  Truck,
  Ship,
  Plane,
} from "lucide-react";
import styles from "../export.module.css";

export interface DeliveryTimeItem {
  region: string;
  days: string;
}

export interface PaymentSectionProps {
  data?: {
    subtitle?: string;
    title: string;
    paymentTitle?: string;
    ttTitle?: string;
    ttDesc?: string;
    lcTitle?: string;
    lcDesc?: string;
    prodTimeTitle?: string;
    prodTimeVal?: string;
    deliveryTimesTitle?: string;
    deliveryTimes?: DeliveryTimeItem[];
  };
}

export default function PaymentSection({ data }: PaymentSectionProps) {
  if (!data || !data.title) return null;

  return (
    <section className={styles.paymentSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <Title
            as="h2"
            size="medium"
            align="center"
            className={styles.sectionTitle}
          >
            {data.title}
          </Title>
        </div>

        <div className={styles.tradeContainer}>
          {/* Top Row: Payment & Production */}
          <div className={styles.tradeTopRow}>
            {/* Payment Terms Column */}
            <div className={styles.tradePaymentCol}>
              {data.paymentTitle && (
                <Title
                  as="h3"
                  size="small"
                  className={styles.tradeBlockTitle}
                >
                  {data.paymentTitle}
                </Title>
              )}
              <div className={styles.tradePaymentMethods}>
                <div className={styles.tradePaymentItem}>
                  <div className={styles.tradePaymentIconWrapper}>
                    <CreditCard size={18} strokeWidth={1.5} />
                  </div>
                  <div className={styles.tradePaymentText}>
                    <span className={styles.paymentMethodTitle}>
                      {data.ttTitle}
                    </span>
                    <Description
                      as="span"
                      size="medium"
                      className={styles.paymentMethodDesc}
                    >
                      {data.ttDesc}
                    </Description>
                  </div>
                </div>
                <div className={styles.tradePaymentItem}>
                  <div className={styles.tradePaymentIconWrapper}>
                    <Clock size={18} strokeWidth={1.5} />
                  </div>
                  <div className={styles.tradePaymentText}>
                    <span className={styles.paymentMethodTitle}>
                      {data.lcTitle}
                    </span>
                    <Description
                      as="span"
                      size="medium"
                      className={styles.paymentMethodDesc}
                    >
                      {data.lcDesc}
                    </Description>
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical border line */}
            <div className={styles.tradeVerticalDivider} />

            {/* Production Lead Time Column */}
            <div className={styles.tradeProductionCol}>
              {data.prodTimeTitle && (
                <Title
                  as="h3"
                  size="small"
                  className={styles.tradeBlockTitle}
                >
                  {data.prodTimeTitle}
                </Title>
              )}
              <div className={styles.tradeProductionValue}>
                <div className={styles.tradeProductionIconWrapper}>
                  <Calendar size={20} strokeWidth={1.5} />
                </div>
                <div className={styles.tradeProductionTimeText}>
                  <span className={styles.tradeProductionDays}>
                    {(data.prodTimeVal || "").replace(/[^\d\s\-\–]/g, "")}
                  </span>
                  <span className={styles.tradeProductionLabel}>
                    {" "}
                    {(data.prodTimeVal || "").replace(/[\d\s\-\–]/g, "")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Horizontal line divider */}
          <div className={styles.tradeHorizontalDivider} />

          {/* Bottom Row: Estimated Delivery Time */}
          <div className={styles.tradeBottomRow}>
            {data.deliveryTimesTitle && (
              <Title
                as="h3"
                size="small"
                className={styles.tradeBlockTitle}
              >
                {data.deliveryTimesTitle}
              </Title>
            )}
            <div className={styles.tradeDeliveryGrid}>
              {(data.deliveryTimes || []).map((item, idx) => {
                const Icon = [Globe, Truck, Ship, Plane][idx] || Globe;
                return (
                  <React.Fragment key={idx}>
                    {idx > 0 && <div className={styles.tradeDeliveryDivider} />}
                    <div className={styles.tradeDeliveryColItem}>
                      <div className={styles.tradeDeliveryIconWrapper}>
                        <Icon size={18} strokeWidth={1.5} />
                      </div>
                      <span className={styles.tradeDeliveryRegion}>
                        {item.region}
                      </span>
                      <span className={styles.tradeDeliveryDays}>
                        {item.days}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
